import { type ReactNode, Children, isValidElement, cloneElement } from 'react';
import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  type FormControlProps,
  type FormLabelProps,
} from '@chakra-ui/react';

import { useValidationSchema, useValidationEntity } from '../hooks';
import MarketRequiredIndicator from './MarketRequiredIndicator';
import type { ItemPath } from 'api/quote-marketplace';

type FieldValue = string | number | boolean | null | undefined;

interface ValidatedFieldProps {
  itemPath: ItemPath;
  fieldPath: string;
  label: string;
  entity?: Record<string, unknown>;
  helperText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  children: ReactNode;
  labelSize?: string;
  formControlProps?: Partial<FormControlProps>;
  labelProps?: Partial<FormLabelProps>;
}

const hasValue = (value: FieldValue): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return true;
  return true;
};

const getChildValue = (children: ReactNode): FieldValue => {
  const child = Children.toArray(children)[0];
  if (isValidElement(child) && child.props) {
    return child.props.value;
  }
  return undefined;
};

const ValidatedField = ({
  itemPath,
  fieldPath,
  label,
  entity,
  helperText,
  isDisabled,
  isInvalid: isInvalidProp,
  errorMessage,
  children,
  labelSize = 'sm',
  formControlProps,
  labelProps,
}: ValidatedFieldProps) => {
  const { isFieldRequired, isFieldRequiredByMarket } = useValidationSchema();
  const contextEntity = useValidationEntity();

  const resolvedEntity = entity ?? contextEntity ?? {};

  const isUniversalRequired = isFieldRequired(
    itemPath,
    fieldPath,
    resolvedEntity,
  );
  const isMarketRequired = isFieldRequiredByMarket(
    itemPath,
    fieldPath,
    resolvedEntity,
  );
  const isRequired = isUniversalRequired || isMarketRequired;

  const value = getChildValue(children);
  const fieldHasValue = hasValue(value);
  const isInvalid = (isRequired && !fieldHasValue) || Boolean(isInvalidProp);

  const child = Children.only(children);
  const childWithProps = isValidElement<{ isInvalid?: boolean }>(child)
    ? cloneElement(child, { isInvalid })
    : child;

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      {...formControlProps}
    >
      <FormLabel fontSize={labelSize} whiteSpace="nowrap" {...labelProps}>
        {label}
      </FormLabel>
      {childWithProps}
      {!fieldHasValue && (
        <MarketRequiredIndicator
          itemPath={itemPath}
          fieldPath={fieldPath}
          entity={resolvedEntity}
        />
      )}
      {isInvalidProp && errorMessage && (
        <FormErrorMessage fontSize="xs">{errorMessage}</FormErrorMessage>
      )}
      {helperText && (
        <FormHelperText fontSize="xs">{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ValidatedField;
